'use client';
import { useEffect } from 'react';
import { loadPostHog } from '@/lib/posthog';

// Load the analytics SDK once the browser is idle after hydration, or on the
// first pointer/key interaction if that comes sooner: a click's pointerdown
// fires before its handler, so capture-then-navigate handlers usually find the
// SDK ready. Events fired earlier are queued by '@/lib/posthog'.
export function PHProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const load = () => void loadPostHog();
    const opts = { once: true, capture: true, passive: true } as const;
    window.addEventListener('pointerdown', load, opts);
    window.addEventListener('keydown', load, opts);
    const removeListeners = () => {
      window.removeEventListener('pointerdown', load, opts);
      window.removeEventListener('keydown', load, opts);
    };

    // Safari has no requestIdleCallback; fall back to a macrotask.
    if (!('requestIdleCallback' in window)) {
      const timerId = setTimeout(load, 1);
      return () => {
        clearTimeout(timerId);
        removeListeners();
      };
    }
    const idleId = window.requestIdleCallback(load, { timeout: 3000 });
    return () => {
      window.cancelIdleCallback(idleId);
      removeListeners();
    };
  }, []);

  return <>{children}</>;
}
